import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Truck, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const procurementCategories = [
  { value: 'industrial', label: 'Material Industrial' },
  { value: 'petroleum', label: 'Peças Petrolíferas' },
  { value: 'technology', label: 'Peças Tecnológicas' },
  { value: 'heavy_equipment', label: 'Heavy Equipment' },
  { value: 'safety', label: 'Safety Equipment' },
  { value: 'other', label: 'Other' },
];

const urgencyLevels = [
  { value: 'standard', label: 'Standard (2-4 weeks)' },
  { value: 'express', label: 'Express (1-2 weeks)' },
  { value: 'urgent', label: 'Urgent (< 1 week)' },
];

export default function ProcurementPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    company_name: '', contact_name: '', contact_email: '', contact_phone: '',
    category: '', item_description: '', quantity: '', unit: 'units',
    specifications: '', preferred_brands: '', urgency: 'standard',
    delivery_location: '', budget_range: '', additional_notes: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/quotes`, {
        product_category: 'procurement',
        product_name: `Procurement: ${form.category} - ${form.item_description}`,
        quantity: parseInt(form.quantity) || 1,
        details: `Company: ${form.company_name}\nContact: ${form.contact_name}\nCategory: ${form.category}\nItem: ${form.item_description}\nQty: ${form.quantity} ${form.unit}\nSpecs: ${form.specifications}\nBrands: ${form.preferred_brands}\nUrgency: ${form.urgency}\nDelivery: ${form.delivery_location}\nBudget: ${form.budget_range}\nNotes: ${form.additional_notes}`,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        company_name: form.company_name,
      });
      setSubmitted(true);
    } catch {} finally { setLoading(false); }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '12px 14px', border: '1px solid var(--border)', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const, background: 'white', fontFamily: 'var(--font-body)' };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' };

  if (submitted) {
    return (
      <><Header />
        <main style={{ paddingTop: 72, minHeight: '100vh', background: 'var(--bg-section)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div data-testid="procurement-success" style={{ background: 'white', border: '1px solid var(--border)', padding: '64px 48px', textAlign: 'center', maxWidth: 500 }}>
            <CheckCircle size={56} style={{ color: 'var(--primary)', marginBottom: 20 }} />
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Procurement Request Submitted</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 15, marginBottom: 32 }}>Our procurement team will analyze your request and get back to you within 24-48 hours.</p>
            <button onClick={() => { setSubmitted(false); setForm({company_name:'',contact_name:'',contact_email:'',contact_phone:'',category:'',item_description:'',quantity:'',unit:'units',specifications:'',preferred_brands:'',urgency:'standard',delivery_location:'',budget_range:'',additional_notes:''}); }}
              style={{ padding: '14px 32px', background: 'var(--primary)', color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer', textTransform: 'uppercase' }}>
              New Request
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main style={{ paddingTop: 72, minHeight: '100vh', background: 'var(--bg-section)' }}>
        <div style={{ background: 'linear-gradient(135deg, #1A237E 0%, #0D47A1 100%)', padding: '64px 24px', textAlign: 'center' }}>
          <Truck size={40} style={{ color: 'rgba(255,255,255,0.8)', marginBottom: 16 }} strokeWidth={1.5} />
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(28px,4vw,44px)', fontWeight: 700, color: 'white', marginBottom: 12 }}>{t('services.procurement.title')}</h1>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.8)', maxWidth: 550, margin: '0 auto' }}>Professional procurement and sourcing services for industrial equipment, petroleum parts, and technology components.</p>
        </div>

        <div style={{ maxWidth: 780, margin: '0 auto', padding: '48px 24px' }}>
          <div style={{ background: 'white', border: '1px solid var(--border)', padding: '40px' }}>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Procurement Quote Request</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 32 }}>Provide detailed information for accurate sourcing and pricing.</p>

            <form onSubmit={handleSubmit} data-testid="procurement-form">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Company info */}
                <div style={{ padding: '20px', background: 'var(--bg-section)', border: '1px solid var(--border)' }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Company Information</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="form-2col">
                    <div><label style={labelStyle}>Company Name *</label><input data-testid="proc-company" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} required style={inputStyle} /></div>
                    <div><label style={labelStyle}>Contact Person *</label><input data-testid="proc-contact" value={form.contact_name} onChange={e => setForm({...form, contact_name: e.target.value})} required style={inputStyle} /></div>
                    <div><label style={labelStyle}>Email *</label><input data-testid="proc-email" type="email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} required style={inputStyle} /></div>
                    <div><label style={labelStyle}>Phone *</label><input data-testid="proc-phone" value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} required style={inputStyle} /></div>
                  </div>
                </div>

                {/* Item details */}
                <div style={{ padding: '20px', background: 'var(--bg-section)', border: '1px solid var(--border)' }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Item Details</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                      <label style={labelStyle}>Category *</label>
                      <select data-testid="proc-category" value={form.category} onChange={e => setForm({...form, category: e.target.value})} required style={{...inputStyle, cursor: 'pointer'}}>
                        <option value="">Select category...</option>
                        {procurementCategories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                    <div><label style={labelStyle}>Item Description *</label><textarea data-testid="proc-item" value={form.item_description} onChange={e => setForm({...form, item_description: e.target.value})} required rows={3} placeholder="Describe the items you need..." style={{...inputStyle, resize: 'vertical' as const}} /></div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="form-2col">
                      <div><label style={labelStyle}>Quantity *</label><input data-testid="proc-qty" type="number" min={1} value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} required style={inputStyle} /></div>
                      <div><label style={labelStyle}>Unit</label>
                        <select value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} style={{...inputStyle, cursor: 'pointer'}}>
                          {['units', 'pieces', 'sets', 'lots', 'meters', 'kg', 'tons'].map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                    </div>
                    <div><label style={labelStyle}>Technical Specifications</label><textarea data-testid="proc-specs" value={form.specifications} onChange={e => setForm({...form, specifications: e.target.value})} rows={3} placeholder="Part numbers, dimensions, material requirements..." style={{...inputStyle, resize: 'vertical' as const}} /></div>
                    <div><label style={labelStyle}>Preferred Brands / Manufacturers</label><input value={form.preferred_brands} onChange={e => setForm({...form, preferred_brands: e.target.value})} placeholder="e.g., Caterpillar, Siemens, ABB..." style={inputStyle} /></div>
                  </div>
                </div>

                {/* Delivery */}
                <div style={{ padding: '20px', background: 'var(--bg-section)', border: '1px solid var(--border)' }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>Delivery & Budget</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                      <label style={labelStyle}>Urgency *</label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {urgencyLevels.map(u => (
                          <button key={u.value} type="button" onClick={() => setForm({...form, urgency: u.value})}
                            style={{ flex: 1, padding: '10px 8px', fontSize: 12, fontWeight: 600, border: `2px solid ${form.urgency === u.value ? 'var(--primary)' : 'var(--border)'}`, background: form.urgency === u.value ? 'rgba(13,71,161,0.08)' : 'white', color: form.urgency === u.value ? 'var(--primary)' : 'var(--text-secondary)', cursor: 'pointer' }}>
                            {u.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="form-2col">
                      <div><label style={labelStyle}>Delivery Location</label><input value={form.delivery_location} onChange={e => setForm({...form, delivery_location: e.target.value})} placeholder="City, Country" style={inputStyle} /></div>
                      <div><label style={labelStyle}>Budget Range</label><input value={form.budget_range} onChange={e => setForm({...form, budget_range: e.target.value})} placeholder="e.g., $5,000 - $10,000" style={inputStyle} /></div>
                    </div>
                    <div><label style={labelStyle}>Additional Notes</label><textarea value={form.additional_notes} onChange={e => setForm({...form, additional_notes: e.target.value})} rows={3} style={{...inputStyle, resize: 'vertical' as const}} /></div>
                  </div>
                </div>

                <button type="submit" data-testid="procurement-submit-btn" disabled={loading}
                  style={{ padding: '16px', background: 'var(--primary)', color: 'white', fontWeight: 700, fontSize: 15, border: 'none', textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', opacity: loading ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <Truck size={18} /> Submit Procurement Request
                </button>
              </div>
            </form>
          </div>
        </div>

        <style>{`
          @media (max-width: 600px) {
            .form-2col { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </main>
      <Footer />
    </>
  );
}
